/**
 * RequireSubscription — the dashboard gate.
 *
 * Decides from get_user_billing_info (see lib/billingAccess.ts):
 *   - admin / full access        → every page
 *   - plan ended, in 30-day grace → every page (banner; creating is disabled
 *                                   in the pages, the server enforces it)
 *   - suspended / expired / never paid / deletion pending
 *                                → only Billing, Account and Support; every
 *                                   other page redirects to Billing (or to
 *                                   Account for a pending deletion) with the
 *                                   reason in the navigation state.
 *
 * While auth or the billing row is loading we show a spinner — never a
 * paywall flash (react-query v5: a disabled query is pending, not loading).
 * No billing row yet (a brand-new profile, Clerk webhook / invitation still
 * on its way) also counts as loading for ~10 s.
 * If the billing RPC fails, the decision falls back to a direct profiles
 * read (role, status) like the old gate did — admins always pass.
 * No DEV bypass: admins always pass, hosts get the real rules everywhere.
 */
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button, Card, CardContent } from '@/components/ui';
import { useBillingInfo, type BillingInfo } from '@/hooks/useSubscription';
import { useAuthReady } from '@/hooks/useAuthReady';
import { getSupabase } from '@/lib/supabase';
import { decideAccess, isLimitedAccessPath, type BlockReason } from '@/lib/billingAccess';

/** How long a missing billing row counts as "still being created". */
const NEW_PROFILE_WAIT_MS = 10_000;

/** The minimal row the fallback reads (the old gate's columns). */
interface ProfileAccessRow {
  role: string | null;
  subscription_status: string | null;
  subscription_plan: string | null;
  trial_ends_at: string | null;
  current_period_end: string | null;
  deleted_at: string | null;
}

function fallbackBillingInfo(row: ProfileAccessRow): BillingInfo {
  // No has_access / portal_open → billingAccess uses the plain status rule.
  return {
    subscription_status: row.subscription_status ?? 'free',
    subscription_plan: row.subscription_plan ?? 'free',
    billing_interval: 'year',
    paid_units: 0,
    current_units: 0,
    current_period_end: row.current_period_end,
    trial_ends_at: row.trial_ends_at,
    is_trialing: false,
    days_until_renewal: null,
    monthly_equivalent: 0,
    stripe_customer_id: null,
    role: row.role ?? 'landlord',
    deleted_at: row.deleted_at,
  };
}

interface RequireSubscriptionProps {
  children: React.ReactNode;
}

/** Navigation state Billing / Account read to explain the redirect. */
export interface BillingRedirectState {
  billingBlocked?: BlockReason | null;
  from?: string;
}

export function RequireSubscription({ children }: RequireSubscriptionProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { isLoaded: userLoaded } = useUser();
  const { userId, isReady } = useAuthReady();
  const billing = useBillingInfo();

  // A missing row right after sign-up: wait a little before deciding "no plan".
  const rowMissing = billing.data === null;
  const [missingTooLong, setMissingTooLong] = useState(false);
  useEffect(() => {
    if (!rowMissing) return;
    const id = setTimeout(() => setMissingTooLong(true), NEW_PROFILE_WAIT_MS);
    return () => clearTimeout(id);
  }, [rowMissing]);

  // The billing RPC failed → read the profile directly (old gate's check).
  const billingFailed = billing.isError && !billing.data;
  const fallback = useQuery({
    queryKey: ['profile-access-check', userId],
    queryFn: async (): Promise<ProfileAccessRow | null> => {
      const { data, error } = await getSupabase()
        .from('profiles')
        .select('role, subscription_status, subscription_plan, trial_ends_at, current_period_end, deleted_at')
        .eq('id', userId as string)
        .maybeSingle();
      if (error) throw error;
      return (data as ProfileAccessRow | null) ?? null;
    },
    enabled: billingFailed && !!userId,
    retry: 1,
    staleTime: 60 * 1000,
  });

  const loading =
    !userLoaded || !isReady || billing.isPending ||
    (rowMissing && !missingTooLong) ||
    (billingFailed && fallback.isPending && fallback.fetchStatus !== 'idle');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-cyan-500" />
          <p className="text-[hsl(var(--muted-foreground))]">
            {t('subscription.required.checking', 'Provjera pretplate...')}
          </p>
        </div>
      </div>
    );
  }

  // Billing RPC failed but the profile could be read: decide from that.
  if (billingFailed && fallback.data) {
    const d = decideAccess(fallbackBillingInfo(fallback.data), location.pathname);
    if (!d.allow && d.redirectTo) {
      const state: BillingRedirectState = { billingBlocked: d.reason, from: location.pathname };
      return <Navigate to={d.redirectTo} replace state={state} />;
    }
    return <>{children}</>;
  }

  // Both checks failed (network, expired session). Billing / Account /
  // Support stay reachable; elsewhere offer a retry instead of a paywall.
  if (billingFailed) {
    if (isLimitedAccessPath(location.pathname)) return <>{children}</>;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center space-y-4">
            <p className="font-semibold">
              {t('subscription.required.checkFailedTitle', 'Ne možemo provjeriti tvoj plan')}
            </p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              {t('subscription.required.checkFailedBody', 'Provjeri internetsku vezu pa pokušaj ponovno. Tvoji podaci su na sigurnom.')}
            </p>
            <Button
              onClick={() => { billing.refetch(); fallback.refetch(); }}
              disabled={billing.isFetching}
              className="gap-2"
            >
              {billing.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {t('subscription.required.retry', 'Pokušaj ponovno')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const decision = decideAccess(billing.data, location.pathname);

  if (!decision.allow && decision.redirectTo) {
    const state: BillingRedirectState = { billingBlocked: decision.reason, from: location.pathname };
    return <Navigate to={decision.redirectTo} replace state={state} />;
  }

  return <>{children}</>;
}

export default RequireSubscription;
